/**************
 Global
 ***************/

// get index of a specific element
function getElIndex(el) {
    let i;
    for (i = 0; el = el.previousElementSibling; i++) ;
    return i;
}

/* ── Adding to the cart, and the order the lines come back in ──────────────────
   Shopify lists cart lines NEWEST FIRST, and /cart/add.js adds the entries of
   `items` one at a time — so the array arrives in the cart REVERSED: the last
   entry leads the cart, and leads the checkout summary. Every gift-bearing caller
   in this theme writes the array the way a person reads it, purchased item first
   and its free gifts after (main-product-v*.js appends the ATC's `data-free` list
   to `items`), which is exactly why the two ebooks were sitting ABOVE the
   subscription the shopper had just bought.

   So reverse it on the way out and the cart reads back the way the caller wrote
   it. That is the whole ordering fix, and it lives here rather than in a dozen buy
   boxes so every caller — including the next one — inherits it. Nothing else about
   the request changes: same variants, quantities, selling plans and properties,
   same endpoint, one round trip.

   Two things ride along, because they need the same one look at the cart:

   - A subscription add that carries no gifts of its own gets the configured gifts
     added IN THE SAME request, ahead of the purchased line. They used to arrive
     later, from the drawer's handleFreeGifts(), and "later" always means on top.
     One request also means a gift can never be left in the cart without the
     subscription that earns it — /cart/add.js takes the whole batch or none of it.
   - Gift variants already in the cart are dropped from the request, so a second
     Add To Cart cannot push an ebook to quantity 2.

   Both are scoped to `window.ncdFreeGifts` — the cart drawer's "Free gifts"
   product setting, the same list handleFreeGifts reconciles against. No SKU, title
   or variant id is hard-coded here, and an empty setting turns all of it off. */

function ncdGiftVariantIds() {
    return (window.ncdFreeGifts || []).map(Number).filter(function (id) { return !!id; });
}

// The two payload shapes /cart/add.js accepts, as this theme writes them. FormData
// posts (the legacy <form> path) are left alone — they can only carry one line anyway.
function cartAddItems(formData) {
    if (!formData || typeof formData !== 'object') return null;
    if (typeof FormData !== 'undefined' && formData instanceof FormData) return null;
    if (Array.isArray(formData.items)) return formData.items;
    if (formData.id !== undefined) return [formData];
    return null;
}

// `selling_plan: null` is how the buy boxes say "one-time", and at least one of them
// stringifies it first, so test for a usable value rather than for the key.
function cartItemIsSubscription(item) {
    var plan = item && item.selling_plan;
    return !!plan && plan !== 'null' && String(plan).trim() !== '';
}

// Variant ids currently in the cart. Authoritative read, not the drawer's cached copy:
// getting this wrong adds a second ebook rather than showing a stale number.
function cartVariantIdsInCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
            /* Throw rather than read a body we do not trust. An error page parses as
               perfectly good JSON with no `items` in it, which would read as "the cart is
               empty" and add a second copy of a gift the shopper already has. Nobody
               downstream is hurt by the throw: addProduct falls back to the caller's own
               payload, and the drawer reconciles the gifts on its next open. */
            if (!r.ok) throw new Error('Cart read failed: ' + r.status);
            return r.json();
        })
        .then(function (cart) {
            var ids = {};
            (cart.items || []).forEach(function (item) { ids[Number(item.variant_id)] = true; });
            return ids;
        });
}

/* Returns { body, injected } — the request to send INSTEAD of the caller's, and whether
   it asks for anything they did not — or null for "send theirs untouched", which is also
   what every failure path here falls back to. */
async function buildCartAddPayload(formData) {
    var items = cartAddItems(formData);
    var gifts = ncdGiftVariantIds();
    if (!items || !items.length || !gifts.length) return null;

    var isGift = function (item) { return gifts.indexOf(Number(item.id)) !== -1; };
    var carriesGifts = items.some(isGift);
    var subscribing = items.some(function (item) { return !isGift(item) && cartItemIsSubscription(item); });

    // A single one-time line needs neither the reversal nor the cart read.
    if (!carriesGifts && !subscribing && items.length < 2) return null;

    var inCart = (carriesGifts || subscribing) ? await cartVariantIdsInCart() : {};

    var out = items.filter(function (item) { return !(isGift(item) && inCart[Number(item.id)]); });
    if (!carriesGifts && subscribing) {
        gifts.forEach(function (id) { if (!inCart[id]) out.push({ id: id, quantity: 1 }); });
    }
    // Everything the caller asked for is already in the cart: let their request stand
    // rather than posting an empty one.
    if (!out.length) return null;
    if (out.length === items.length && out.length < 2) return null;

    return {
        body: { items: out.slice().reverse() },      // reversed: Shopify prepends each entry
        injected: out.length > items.length
    };
}

function postCartAdd(payload) {
    return fetch('/cart/add.js', {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });
}

/* In-flight adds, keyed by the request the caller asked for. A second identical click
   while the first is still in the air gets the SAME promise back instead of a second
   POST. The buttons' own `loading` classes are the first line of defence against a
   double tap, but they are set by a dozen separate call sites and the gift look-up
   above makes the window longer than it was. Only genuinely concurrent identical adds
   are merged — anything issued after the first has settled is a real second add and
   still goes through. */
const inflightCartAdds = {};

//add new product to cart
//let formData = { quantity: 1, id: 794864229, properties: { 'Personalize': 'ZHM' } };
function addProduct(formData) {
    var key = null;
    if (!(typeof FormData !== 'undefined' && formData instanceof FormData)) {
        try { key = JSON.stringify(formData); } catch (e) { key = null; }
    }
    if (key && inflightCartAdds[key]) return inflightCartAdds[key];

    const run = (async function () {
        let payload = null;
        try {
            payload = await buildCartAddPayload(formData);
        } catch (e) {
            // A failed /cart.js read must never cost the shopper their add.
            payload = null;
        }

        let res = await postCartAdd(payload ? payload.body : formData);
        if (!res.ok && payload && payload.injected) {
            /* The rewritten request was rejected, and a gift we added on the caller's
               behalf is the only new way that can happen — reordering or dropping a line
               cannot fail an add that would otherwise have succeeded. Ask again for
               exactly what the caller wanted: a free ebook must never be the thing that
               stops someone buying. */
            res = await postCartAdd(formData);
        }
        /* A failed add REJECTS, with Shopify's own words attached — the live PDP
           (new-pdp-main.liquid) catches this and shows the message to the shopper. */
        return res.json().catch(function () { return {}; }).then(function (body) {
            if (!res.ok) {
                var error = new Error(body.description || body.message || 'Unable to add this item to your cart.');
                error.status = res.status;
                error.payload = body;
                throw error;
            }
            return body;
        });
    })();

    if (key) {
        inflightCartAdds[key] = run;
        const clear = function () { if (inflightCartAdds[key] === run) delete inflightCartAdds[key]; };
        run.then(clear, clear);
    }
    return run;
}

//change an existing product in your cart
//let formData = { quantity: 1, id: 794864229 };
function changeProduct(formData) {
    return fetch('/cart/change.js', {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(function (data) {
            return data.json();
        })
}

//update cart note or attributes (_ for attributes are hidden and __ are private)
//let formData = { note: 'This is a note about my order', attributes: { 'Gift wrap': 'Yes' } }
function updateCart(formData) {
    return fetch('/cart/update.js', {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(function (data) {
            return data.json();
        })
}

//download product
function downloadProduct(handle) {
    return fetch(`/products/${handle}.js`)
        .then(function (data) {
            return data.json();
        })
        .then(function (data) {
            return data;
        })
        .catch((error) => {
            return false;
        });
}


// print price ($51.27 = 5127)
function price(amount) {
    let total = (parseFloat(amount) / 100).toFixed(2);
    return `$${total.replace(".00", "")}`;
}

// Smooth in-page anchors
(function () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const DURATION = 1000;
    var rafId = null;

    function doScrolling(elementY, duration) {
        var startingY = window.pageYOffset;
        var diff = elementY - startingY;
        var start;

        if (rafId) cancelAnimationFrame(rafId);

        window.requestAnimationFrame(function step(timestamp) {
            if (!start) start = timestamp;
            var time = timestamp - start;
            var percent = Math.min(time / duration, 1);

            // Must be auto/instant: otherwise CSS scroll-behavior fights each frame
            window.scrollTo({
                top: startingY + diff * percent,
                left: 0,
                behavior: 'auto'
            });

            if (time < duration) {
                rafId = window.requestAnimationFrame(step);
            } else {
                rafId = null;
            }
        });
    }

    document.addEventListener('click', function (event) {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href || href === '#') return;

        const id = href.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();

        const pad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        doScrolling(window.pageYOffset + target.getBoundingClientRect().top - pad, DURATION);

        if (history.replaceState) history.replaceState(null, '', '#' + id);
    });
})();

// iOS Safari does not apply :active until the document carries a touch listener.
// Verified on a real iPhone 2026-07-31: without this the product card's press
// feedback (.n-pcard__image-wrap:active, assets/custom_s.css) never fires, even
// though it works in Chromium's touch emulation. Passive, so it cannot delay
// scrolling, and it lives in a file that already loads on every page.
document.addEventListener('touchstart', function () {}, { passive: true });
